import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  styled
} from '@mui/material';
import CustomAppBar from './CustomAppBar';
import { httpApi } from './api';
import moment from 'moment';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  padding: '12px 16px',
  fontFamily: "'Roboto Mono', monospace",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: '#e0e0e0',
}));

const GradientHeader = styled(TableRow)(({ theme }) => ({
  background: 'linear-gradient(45deg, #1a5fb4 30%, #2a3f9e 90%)',
  borderBottom: '2px solid #00ffaa80',
}));

const HoverRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#1a237e30',
    transform: 'scale(1.002)',
    transition: 'all 0.1s ease-in-out',
  },
}));

const SignalIndicator = styled('span')(({ signal }) => ({
  color: signal === 'Buy' ? '#4CAF50' : signal === 'Sell' ? '#F44336' : '#FFEB3B',
  filter: 'drop-shadow(0 0 2px currentColor)',
  fontSize: '1.2rem',
}));

const PulseDot = styled('div')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  animation: 'pulse 1.5s infinite',
  marginRight: '8px',
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(0, 255, 170, 0.7)' },
    '70%': { boxShadow: '0 0 0 6px rgba(0, 255, 170, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(0, 255, 170, 0)' },
  },
});

const TrafficLight = styled('div')(({ sideways }) => ({
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  backgroundColor: sideways === 'True' ? '#F44336' : '#4CAF50', // Red for sideways, Green for momentum
  filter: 'drop-shadow(0 0 2px currentColor)',
  margin: '0 auto',
  animation: sideways === 'False' ? 'pulse 1.5s infinite' : 'none',
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
    '70%': { boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
  },
}));

const formatNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? value : num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getPriceIndicator = (currentValue, comparisonValue) => {
  const current = Number(currentValue);
  const comparison = Number(comparisonValue);
  
  if (isNaN(current) || isNaN(comparison)) return <span>N/A</span>;

  const diff = current - comparison;
  let color = '#bdbdbd';
  let indicator = '━';

  if (diff > 0) {
    color = '#4CAF50';
    indicator = '▲';
  } else if (diff < 0) {
    color = '#F44336';
    indicator = '▼';
  }

  return (
    <span style={{ color }}>
      {formatNumber(current)} {indicator}
    </span>
  );
};

const Signal = () => {
  const [tradeSignals, setTradeSignals] = useState([]);
  const [orderBy, setOrderBy] = useState('date');
  const [orderDirection, setOrderDirection] = useState('desc');

  const fetchTradeSignals = async () => {
    try {
      const response = await httpApi.get('/api/trade-signals');
      const data = response.data;
      
      const withPrevious = data.map((entry, index, arr) => ({
        ...entry,
        previous_close: index > 0 ? arr[index - 1].current_close : null
      }));

      setTradeSignals(withPrevious);
    } catch (error) {
      console.error('Error fetching trade signals:', error);
    }
  };

  useEffect(() => {
    fetchTradeSignals();
    const intervalId = setInterval(fetchTradeSignals, 300000);
    return () => clearInterval(intervalId);
  }, []);

  const handleSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderBy(property);
    setOrderDirection(isAsc ? 'desc' : 'asc');
  };

  const sortedData = [...tradeSignals].sort((a, b) => {
    const aVal = orderBy === 'date' ? moment(a.date, 'DD-MMM-YYYY HH:mm').valueOf() : a[orderBy];
    const bVal = orderBy === 'date' ? moment(b.date, 'DD-MMM-YYYY HH:mm').valueOf() : b[orderBy];

    return orderDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const getSignalDisplay = (signalValue) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <PulseDot style={{ 
        backgroundColor: signalValue === 'Buy' ? '#4CAF50' : '#F44336',
        animationPlayState: signalValue ? 'running' : 'paused'
      }}/>
      <SignalIndicator signal={signalValue}>
        {signalValue === 'Buy' ? 'BUY' : signalValue === 'Sell' ? 'SELL' : 'EXIT'}
      </SignalIndicator>
    </div>
  );

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xl" sx={{
          borderRadius: '8px',
          border: '1px solid #00ffaa30',
          boxShadow: '0 0 20px #00ffaa20',
          background: '#0a1929',
        }}>
        <TableContainer 
          component={Paper}
          sx={{
            background: '#0a1929',
            border: '1px solid #00ffaa30',
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1400 }}>
            <TableHead>
              <GradientHeader>
                {[
                  { label: 'Date', prop: 'date', width: '160px' },
                  { label: 'Sideways', prop: 'sideways_60min', align: 'center' },
                  { label: 'Close', prop: 'current_close', align: 'right' },
                  { label: 'Next Close', prop: 'predicted_next_hour_close_price', align: 'right' },
                  { label: 'High-Low', prop: 'hl_signal', align: 'center' },
                  { label: 'MACD', prop: 'macd_signal', align: 'center' },
                  { label: 'MACD 15', prop: 'macd_signal_15', align: 'center' },
                  { label: 'Super Trend', prop: 'st_signal', align: 'center' },
                  { label: 'Final Signal', prop: 'final_signal', align: 'center' },
                  { label: 'Points', prop: 'gained_points', align: 'right' },
                ].map((head) => (
                  <StyledTableCell
                    key={head.label}
                    align={head.align || 'left'}
                  >
                    <TableSortLabel
                      active={orderBy === head.prop}
                      direction={orderDirection}
                      onClick={() => handleSort(head.prop)}
                    >
                      {head.label}
                    </TableSortLabel>
                  </StyledTableCell>
                ))}
              </GradientHeader>
            </TableHead>
            <TableBody>
              {sortedData.map((signal, index) => (
                <HoverRow key={index}>
                  <StyledTableCell sx={{ color: '#00ffaa' }}>
                    {moment(signal.date, 'DD-MMM-YYYY HH:mm').format('DD MMM YY HH:mm')}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    <TrafficLight sideways={signal.sideways_60min} />
                  </StyledTableCell>

                  <StyledTableCell align="right">
                    {getPriceIndicator(signal.current_close, signal.previous_close)}
                  </StyledTableCell>

                  <StyledTableCell align="right">
                    {getPriceIndicator(
                      signal.predicted_next_hour_close_price,
                      signal.current_close
                    )}
                  </StyledTableCell>
                      
                  <StyledTableCell align="center">
                    {getSignalDisplay(signal.hl_signal)}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    {getSignalDisplay(signal.macd_signal)}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    {getSignalDisplay(signal.macd_signal_15)}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    {getSignalDisplay(signal.st_signal)}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    {getSignalDisplay(signal.final_signal)}
                  </StyledTableCell>

                  <StyledTableCell align="right" sx={{ 
                    color: signal.gained_points >= 0 ? '#4CAF50' : '#F44336',
                    fontWeight: 900 
                  }}>
                    {formatNumber(signal.gained_points)}
                  </StyledTableCell>
                </HoverRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
};

export default Signal;